import { Box, Grid, Skeleton } from '@mui/material';
import React from 'react';
import CardView from '../../../component/card/card-view';

/** 餐厅建档骨架屏支持的页面模式。 */
export type RestaurantInfoSkeletonComponentType = 'ADD' | 'EDIT' | 'PREVIEW' | 'BASE_INFO';

/** 餐厅核心分区的锚点占位宽度。 */
const RESTAURANT_SKELETON_ANCHOR_WIDTHS = [96, 104, 88, 88] as const;

/** 餐厅身份区编辑态的宽屏字段栅格。 */
const RESTAURANT_EDIT_IDENTITY_SKELETON_SPANS = [2, 6, 6, 6, 6, 6, 6, 12] as const;

/** 餐厅身份区新增态的宽屏字段栅格。 */
const RESTAURANT_ADD_IDENTITY_SKELETON_SPANS = [2, 6, 6, 6, 6, 12] as const;

/** 餐厅税务、联系人和商务分区的宽屏字段栅格。 */
const RESTAURANT_SKELETON_SECTION_SPANS = [
    [12, 4, 4, 4, 12, 12],
    [12, 4, 2, 6, 6, 6, 12, 4, 2, 6],
    [4, 4, 4, 4, 4, 4, 4, 12],
] as const;

/**
 * 渲染餐厅编辑态的工作台骨架屏。
 * @param componentType 餐厅表单当前展示模式
 * @returns 与餐厅建档工作台布局一致的加载占位
 */
export function SkeletonOfRestaurantInfo({
    componentType = 'EDIT',
    countryCode = '',
}: Readonly<{
    componentType?: RestaurantInfoSkeletonComponentType;
    countryCode?: string;
}>): JSX.Element {
    /** 当前模式下身份分区的字段栅格。 */
    const identitySpans = componentType === 'ADD'
        ? RESTAURANT_ADD_IDENTITY_SKELETON_SPANS
        : RESTAURANT_EDIT_IDENTITY_SKELETON_SPANS;
    /** 当前国家是否展示意大利专属电子发票字段。 */
    const isItaly = countryCode === '+39';
    /** 当前模式和国家下税务分区的字段栅格。 */
    const taxAndAddressSpans = isItaly
        ? RESTAURANT_SKELETON_SECTION_SPANS[0]
        : [12, 4, 4, 12, 12] as const;
    /** 当前模式和国家下四个核心分区的骨架字段栅格。 */
    const skeletonSections = [
        { key: 'identity', spans: identitySpans },
        { key: 'tax-and-address', spans: taxAndAddressSpans },
        { key: 'contacts', spans: RESTAURANT_SKELETON_SECTION_SPANS[1] },
        { key: 'business', spans: RESTAURANT_SKELETON_SECTION_SPANS[2] },
    ] as const;

    return (
        <Box
            className='skeleton archive-form-skeleton'
        >
            <Box className='archive-form-skeleton__anchors' data-testid='restaurant-info-skeleton-anchors'>
                {RESTAURANT_SKELETON_ANCHOR_WIDTHS.map((width, index) => (
                    <Skeleton
                        key={`${width}-${index}`}
                        variant='rounded'
                        width={width}
                        height={32}
                        animation='wave'
                    />
                ))}
            </Box>
            <Grid container rowSpacing={2}>
                {skeletonSections.map(({ key, spans }) => (
                    <Grid item xs={12} key={key}>
                        <Box
                            data-testid='restaurant-info-skeleton-section'
                            data-field-count={spans.length}
                        >
                            <SkeletonSectionCard spans={spans} identity={key === 'identity'} />
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

/**
 * 渲染单个餐厅建档分区的骨架卡片。
 * @param spans 当前分区的宽屏字段栅格
 * @param identity 是否为身份分区
 * @returns 带标题和字段占位的分区卡片
 */
function SkeletonSectionCard({
    spans,
    identity,
}: Readonly<{
    spans: readonly number[];
    identity: boolean;
}>): JSX.Element {
    return (
        <CardView
            className='archive-form-skeleton__card'
            contentRender={(
                <Grid container rowSpacing={2}>
                    <Grid item xs={12}>
                        <Skeleton variant='rounded' width={150} height={24} animation='wave' />
                    </Grid>
                    <Grid item xs={12}>
                        <SkeletonFieldGrid spans={spans} identity={identity} />
                    </Grid>
                </Grid>
            )}
        />
    );
}

/**
 * 渲染分区内的字段占位网格。
 * @param spans 当前分区字段在宽屏下占用的列数
 * @param identity 是否在首个位置渲染 64px Logo 占位
 * @returns 响应式字段占位网格
 */
function SkeletonFieldGrid({
    spans,
    identity,
}: Readonly<{
    spans: readonly number[];
    identity: boolean;
}>): JSX.Element {
    return (
        <Grid container spacing={2}>
            {spans.map((span, index) => (
                <Grid item xs={12} md={span} key={`${span}-${index}`}>
                    {identity && index === 0 ? (
                        <Skeleton
                            data-testid='restaurant-info-skeleton-avatar'
                            variant='rounded'
                            width={64}
                            height={64}
                            animation='wave'
                        />
                    ) : (
                        <Skeleton variant='rounded' height={46} animation='wave' />
                    )}
                </Grid>
            ))}
        </Grid>
    );
}
